import { useCallback, useMemo, useState } from "react";
import Label from "../label/Label";
import { Icon } from "../icons/Icon";
import { cn } from "@/lib/styles.utils";
import type { IconName } from "../icons/icons";
import { literals } from "@/constants/literals";

type BaseInputTextProps = {
  placeholder: string;
  value: string;
  size: "large" | "medium" | "small";
  label?: string;
  disabled?: boolean;
  icon?: IconName;
  name?: string;
  noBottomSpace?: boolean;
  required?: boolean;
  requiredAsterisk?: boolean;
  warning?: boolean;
  warningMessage?: string;
  error?: boolean;
  errorMessage?: string;
  success?: boolean;
  successMessage?: string;
  helpText?: string;
  autoClean?: boolean;
};

type ReadOnlyProps = BaseInputTextProps & {
  readOnly: true;
  onChange?: (data: string) => void;
};

type EditableProps = BaseInputTextProps & {
  readOnly?: false;
  onChange: (data: string) => void;
};

type InputTextProps = ReadOnlyProps | EditableProps;

const InputText = ({
  label,
  placeholder,
  value,
  size,
  onChange,
  disabled,
  readOnly,
  required,
  warning,
  warningMessage,
  error,
  errorMessage,
  success,
  successMessage,
  helpText = "",
  autoClean = true,
  icon,
  name,
  noBottomSpace,
  requiredAsterisk,
}: InputTextProps) => {
  const [showHelpText, setShowHelpText] = useState(false);

  const changeValue = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!readOnly) onChange(event.target.value);
    },
    [onChange, readOnly],
  );

  const identificator = useMemo(() => {
    if (name) return name;
    const base = label || placeholder || "textinput";
    return base
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replaceAll(" ", "-")
      .toLowerCase();
  }, [name, label, placeholder]);

  const requiredAlert = useMemo(
    () => required && value.trim().length === 0,
    [required, value],
  );

  const inputClasses = useMemo(() => {
    let border = "border-ib-gray-borders";
    if (error || requiredAlert) border = "border-ib-pt-color-red-500";
    else if (warning) border = "border-ib-pt-color-orange-500";
    else if (success) border = "border-ib-green-succes";

    return cn(
      "w-full rounded-xs border bg-ib-ib-white text-ib-main focus:outline-none",
      "disabled:border-ib-gray-borders read-only:bg-ib-gray-lightest",
      "px-3 py-2",
      autoClean && !disabled && !readOnly ? "pr-9" : "",
      border,
      size === "small"
        ? "text-sm"
        : size === "medium"
          ? "text-base"
          : "text-lg",
    );
  }, [
    error,
    requiredAlert,
    warning,
    success,
    size,
    autoClean,
    disabled,
    readOnly,
  ]);

  const helpClasses = useMemo(() => {
    if (error || requiredAlert) {
      return {
        icon: "text-ib-pt-color-red-500",
        text: "text-ib-pt-color-red-500",
      };
    }
    if (warning) {
      return {
        icon: "text-ib-pt-color-yellow-800",
        text: "text-ib-pt-color-orange-800",
      };
    }
    if (success) {
      return {
        icon: "text-ib-pt-color-green-800",
        text: "text-ib-pt-color-green-800",
      };
    }
    return {
      icon: "ib-pt-color-gray-800",
      text: "ib-pt-color-gray-800",
    };
  }, [error, warning, success, requiredAlert]);

  const infoMessage = useMemo(() => {
    if (requiredAlert) return literals.required;
    if (warning) return warningMessage || helpText;
    if (error) return errorMessage || helpText;
    if (success) return successMessage || helpText;
    if (helpText && (showHelpText || disabled || readOnly)) return helpText;
    return null;
  }, [
    requiredAlert,
    warning,
    error,
    success,
    helpText,
    showHelpText,
    disabled,
    readOnly,
    warningMessage,
    errorMessage,
    successMessage,
  ]);

  const showOrHideHelpText = () => setShowHelpText((v) => !v);

  return (
    <div className="flex flex-col gap-1 w-full text-sm">
      {label && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Label
              label={label}
              id={identificator}
              required={required || requiredAsterisk}
            />
            {helpText && (
              <button type="button" onClick={showOrHideHelpText}>
                <Icon
                  name="info"
                  className="size-3 text-ib-pt-color-gray-800"
                />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="relative w-full">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <Icon
              name={icon}
              className={cn(
                "size-4",
                disabled || readOnly
                  ? "text-ib-gray-disabled"
                  : "text-ib-pt-color-red-500",
              )}
            />
          </div>
        )}

        <input
          type="text"
          id={identificator}
          name={identificator}
          disabled={disabled}
          readOnly={readOnly}
          required={requiredAlert}
          className={inputClasses}
          placeholder={placeholder}
          onChange={changeValue}
          value={value}
        />

        {autoClean && !disabled && !readOnly && value && (
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2"
            onClick={() => onChange("")}
          >
            <Icon
              name="closeCircle"
              className="size-4 text-ib-pt-color-red-500"
            />
          </button>
        )}
      </div>

      {!noBottomSpace && infoMessage && (
        <div className="flex items-center gap-2 text-xs">
          <Icon name="info" className={cn("size-4", helpClasses.icon)} />
          <span className={helpClasses.text}>{infoMessage}</span>
        </div>
      )}
    </div>
  );
};

export default InputText;
